import React from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Line
} from 'recharts';

interface ChartPoint {
    x: string;
    y: number;
}

interface SalesLineChartProps {
    currentSeries?: ChartPoint[];
    previousSeries?: ChartPoint[];
    targetGoal?: number;
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        notation: 'compact',
        compactDisplay: 'short'
    }).format(value);
};

const SalesLineChart: React.FC<SalesLineChartProps> = ({ currentSeries = [], previousSeries = [], targetGoal }) => {
    if (currentSeries.length === 0) {
        return <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontWeight: 600, fontSize: 13 }}>Sem dados para exibir no gráfico.</div>;
    }

    // Converte série diária em valores acumulados (soma progressiva)
    const toCumulative = (data: ChartPoint[], isCurrent: boolean = false): { x: string; y: number | null }[] => {
        let acc = 0;
        
        let lastSalesIndex = -1;
        if (isCurrent && data.length > 0) {
            const lastPointDate = new Date(data[data.length - 1].x + 'T00:00:00Z');
            const today = new Date();
            
            if (lastPointDate > today) {
                for (let i = data.length - 1; i >= 0; i--) {
                    if (data[i].y > 0) {
                        lastSalesIndex = i;
                        break;
                    }
                }
            }
        }

        return data.map((point, index) => {
            acc += point.y;
            if (isCurrent && lastSalesIndex !== -1 && index > lastSalesIndex) {
                return { x: point.x, y: null };
            }
            return { x: point.x, y: acc };
        });
    };

    const cumulativeCurrent = toCumulative(currentSeries, true);
    const cumulativePrevious = toCumulative(previousSeries, false);

    // Deriva nomes dos períodos a partir das datas dos dados
    const deriveLabels = (current: ChartPoint[], previous: ChartPoint[]) => {
        if (current.length === 0) return { current: 'Atual', previous: 'Anterior' };

        const first = new Date(current[0].x + 'T00:00:00Z');
        const last = new Date(current[current.length - 1].x + 'T00:00:00Z');
        const totalDays = (last.getTime() - first.getTime()) / (1000 * 60 * 60 * 24);

        const monthCap = (d: Date) => {
            const m = d.toLocaleDateString('pt-BR', { timeZone: 'UTC', month: 'long' });
            return m.charAt(0).toUpperCase() + m.slice(1);
        };

        if (totalDays <= 35) {
            // Visão mensal — mostra nome dos meses
            const y = last.getUTCFullYear().toString().slice(-2);
            const currentLabel = `${monthCap(last)}/${y}`;
            const previousLabel = previous.length > 0
                ? `${monthCap(new Date(previous[previous.length - 1].x + 'T00:00:00Z'))}/${y}`
                : 'Mês Anterior';
            return { current: currentLabel, previous: previousLabel };
        } else if (totalDays <= 400) {
            // Visão anual — mostra anos
            const currentLabel = String(last.getUTCFullYear());
            const previousLabel = previous.length > 0
                ? String(new Date(previous[previous.length - 1].x + 'T00:00:00Z').getUTCFullYear())
                : 'Anterior';
            return { current: currentLabel, previous: previousLabel };
        } else {
            // Período custom — genérico
            return { current: 'Atual', previous: 'Anterior' };
        }
    };

    const { current: currentLabel, previous: previousLabel } = deriveLabels(currentSeries, previousSeries);

    // Formata os dados mantendo index-match (backbone já normaliza as séries)
    const formattedData = cumulativeCurrent.map((point, index) => {
        const date = new Date(point.x + 'T00:00:00Z');
        const label = date.toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit' });

        return {
            date: label,
            periodoAtual: point.y,
            periodoAnterior: cumulativePrevious[index]?.y ?? null,
            meta: targetGoal || null,
        };
    });

    // Encontra o index do último ponto não nulo de periodoAtual
    const lastActiveIndex = formattedData.reduce((lastIdx, d, idx) => {
        return d.periodoAtual !== null ? idx : lastIdx;
    }, -1);

    const renderCustomDot = (props: any) => {
        const { cx, cy, index, payload } = props;
        if (index !== lastActiveIndex || payload.periodoAtual === null) return <g />;

        const labelText = formatCurrency(payload.periodoAtual);
        const textWidth = labelText.length * 6.2 + 12;
        const margin = 20;
        const isNearStart = (cx - textWidth - margin) < 0;
        
        const rectX = isNearStart ? cx + margin : cx - textWidth - margin;
        const rectY = cy - 30;
        const textX = rectX + 6;
        const textY = rectY + 14;

        const lineX1 = isNearStart ? cx + 4 : cx - 4;
        const lineY1 = cy - 3;
        const lineX2 = isNearStart ? cx + margin : cx - margin;
        const lineY2 = cy - 20;

        return (
            <g key={index}>
                {/* Linha indicadora diagonal conectando bolinha ao balãozinho suspenso */}
                <line
                    x1={lineX1}
                    y1={lineY1}
                    x2={lineX2}
                    y2={lineY2}
                    stroke="rgba(71, 85, 105, 0.6)"
                    strokeWidth={1}
                />

                <circle cx={cx} cy={cy} r={8} fill="var(--theme-chart-start, #dc2626)" opacity={0.25} />
                <circle cx={cx} cy={cy} r={4.5} fill="var(--theme-chart-start, #dc2626)" stroke="#fff" strokeWidth={1.5} />
                
                <rect
                    x={rectX}
                    y={rectY}
                    width={textWidth}
                    height={20}
                    rx={6}
                    fill="rgba(30, 41, 59, 0.7)"
                    stroke="rgba(71, 85, 105, 0.4)"
                    strokeWidth={1}
                />
                <text
                    x={textX}
                    y={textY}
                    fill="#ffffff"
                    fontSize={10}
                    fontWeight={700}
                    fontFamily="Inter, sans-serif"
                >
                    {labelText}
                </text>
            </g>
        );
    };

    // Domínio do Y
    const allValues = formattedData.flatMap(d => [d.periodoAtual, d.periodoAnterior, d.meta].filter((v): v is number => v !== null));
    const maxValue = Math.max(...allValues, 0);
    const yAxisDomain = [0, Math.ceil(maxValue * 1.1)];

    // Tooltip
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const validPayload = payload.filter((entry: any) => entry.value !== null);
            if (validPayload.length === 0) return null;

            const atualEntry = validPayload.find((entry: any) => entry.dataKey === 'periodoAtual');
            const anteriorEntry = validPayload.find((entry: any) => entry.dataKey === 'periodoAnterior');
            let percentageChange: number | null = null;
            if (atualEntry && anteriorEntry) {
                const currentValue = atualEntry.value;
                const previousValue = anteriorEntry.value;
                if (previousValue !== 0) {
                    percentageChange = ((currentValue - previousValue) / previousValue) * 100;
                }
            }

            return (
                <div style={{
                    background: 'var(--bg-widget)',
                    borderRadius: 'var(--radius-base, 8px)',
                    padding: '12px 16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    border: '1px solid var(--border-primary)',
                    fontSize: 12,
                    minWidth: 200
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                        borderBottom: '1px solid var(--border-primary)',
                        paddingBottom: 8
                    }}>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary, #111827)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            {label}
                        </span>
                        {percentageChange !== null && (
                            <span style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: percentageChange >= 0 ? '#10b981' : '#ef4444',
                                background: percentageChange >= 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-base, 8px)'
                            }}>
                                {percentageChange >= 0 ? '↑' : '↓'} {Math.abs(percentageChange).toFixed(1)}%
                            </span>
                        )}
                    </div>
                    {validPayload.map((entry: any, index: number) => (
                        <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 16,
                            padding: '4px 0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: entry.color,
                                    flexShrink: 0
                                }} />
                                <span style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 500, fontSize: 13 }}>
                                    {entry.name}
                                </span>
                            </div>
                            <span style={{
                                fontWeight: 700,
                                color: 'var(--text-primary, #111827)',
                                fontSize: 14
                            }}>
                                {formatCurrency(entry.value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart
                data={formattedData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fontWeight: 700, fill: '#374151' }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={50}
                />
                <YAxis
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#6B7280' }}
                    tickFormatter={formatCurrency}
                    domain={yAxisDomain}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip
                    content={<CustomTooltip />}
                    offset={20}
                    wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                />
                <Legend
                    wrapperStyle={{ paddingTop: '10px', fontSize: 10, fontWeight: 700 }}
                    iconType="line"
                />
                <defs>
                    <linearGradient id="currentGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--theme-chart-start, #dc2626)" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="var(--theme-chart-start, #dc2626)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="previousGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--theme-accent, #1e293b)" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="var(--theme-accent, #1e293b)" stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                {targetGoal && targetGoal > 0 && (
                    <Line
                        type="monotone"
                        dataKey="meta"
                        name="Meta"
                        stroke="#528265"
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                        activeDot={false}
                        legendType="plainline"
                    />
                )}
                <Area
                    type="monotone"
                    dataKey="periodoAnterior"
                    name={previousLabel}
                    stroke="var(--theme-accent, #1e293b)"
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    fill="url(#previousGrad)"
                    dot={false}
                    connectNulls={true}
                />
                <Area
                    type="monotone"
                    dataKey="periodoAtual"
                    name={currentLabel}
                    stroke="var(--theme-chart-start, #dc2626)"
                    strokeWidth={2.5}
                    fill="url(#currentGrad)"
                    dot={renderCustomDot}
                    activeDot={{ r: 4, fill: 'var(--theme-chart-start, #dc2626)', strokeWidth: 2, stroke: '#fff' }}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default SalesLineChart;
